import React, { useMemo } from 'react'

import { useDataTableColumns, useDataTableSorting } from '@/hooks/ui'

import {
  DataTableColumn,
  DataTablePropsColumns,
  TableSortingController,
} from '@/components/ui/data-table'

interface ClassesProps<Item> {
  column?: DataTableColumn<Item, any>
  data?: Item
}
interface ColumnDefinition<Item, ColumnKeys> {
  accessorKey: ColumnKeys
  columnLabel(): React.ReactNode
  cell(data: Item): React.ReactNode | string | null
  classes?(props: ClassesProps<Item>): {
    column?: string
    cell?: string
  }
  hasSorting?: boolean
  isAction?: boolean
}

interface UseDataTableProps<Item, ColumnKeys> {
  columns: ColumnDefinition<Item, ColumnKeys>[]
  visibleColumns?: ColumnKeys[]
  disabledColumns?: ColumnKeys[]
  dependencies?: any[]
}

interface UseDataTableReturn<Item, ColumnKeys> {
  columns: DataTablePropsColumns<Item, ColumnKeys>
  isColumnVisible: (column: ColumnKeys) => boolean
  changeColumnVisibility: (column: ColumnKeys) => void
  disabledColumns: ColumnKeys[]
  sort: ReturnType<typeof useDataTableSorting<ColumnKeys>>['sort']
  sortingControl: ReturnType<typeof useDataTableSorting<ColumnKeys>>['control']
}

export function useDataTable<Item, ColumnKeys>({
  columns: columnDefinitions,
  visibleColumns: defaultVisibleColumns,
  disabledColumns: defaultDisabledColumns = [],
  dependencies = [],
}: UseDataTableProps<Item, ColumnKeys>): UseDataTableReturn<Item, ColumnKeys> {
  const columnKeys = useMemo(
    () => columnDefinitions.map((col) => col.accessorKey),
    [columnDefinitions]
  )

  const { control: sortingControl, sort } = useDataTableSorting<ColumnKeys>({
    columns: columnKeys,
  })

  const { isColumnVisible, changeColumnVisibility, disabledColumns } = useDataTableColumns({
    columns: columnKeys,
    defaultVisibleColumns,
    defaultDisabledColumns,
  })

  const columns = useMemo(
    () =>
      columnDefinitions.map((col) => ({
        ...col,
        hidden: !isColumnVisible(col.accessorKey),
        sorting: col.hasSorting
          ? () => TableSortingController<ColumnKeys>(col.accessorKey, sortingControl)
          : undefined,
        classes: col?.classes,
        isAction: col?.isAction,
      })),
    [columnDefinitions, isColumnVisible, sortingControl, ...dependencies]
  )

  return {
    columns,
    isColumnVisible,
    changeColumnVisibility,
    disabledColumns,
    sort,
    sortingControl,
  }
}
